import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import axios from "axios";
import {addMonthsISO, buildGroupColorMap, toISODate} from "./format.js";

/**
 * Berechnet die Von/Bis-Grenzen einer Zeitraum-Voreinstellung. Der
 * Bezugspunkt ist bewusst NICHT das heutige Datum, sondern der jüngste Beleg
 * im Datenbestand (meta.date_range.max) - das Alt-ERP "Majesty" wurde zum
 * 30.06.2026 abgelöst, danach kommen keine neuen Belege mehr nach. Mit
 * "heute" als Bezug würde "Letzte 12 Monate" Wochen ohne jeden Beleg
 * enthalten und einen Umsatzeinbruch vortäuschen, der keiner ist.
 */
function computePresetRange(preset, meta) {
    const referenceIso = meta?.date_range?.max;
    if (!referenceIso) {
        return null;
    }
    const referenceYear = Number(referenceIso.slice(0, 4));

    switch (preset) {
        case "last12":
            return {from: addMonthsISO(referenceIso, -12), to: referenceIso};
        case "last24":
            return {from: addMonthsISO(referenceIso, -24), to: referenceIso};
        case "ytd":
            return {from: toISODate(referenceYear, 1, 1), to: referenceIso};
        case "prevYear": {
            const year = referenceYear - 1;
            return {from: toISODate(year, 1, 1), to: toISODate(year, 12, 31)};
        }
        case "all":
            return {from: meta?.date_range?.min || null, to: meta?.date_range?.max || null};
        default:
            return null;
    }
}

/**
 * Gemeinsamer Filterzustand der Verkaufsstatistik-Seiten (Überblick,
 * Sortimente, Artikel, Renner & Penner): Zeitraum, Auswertungsebene,
 * Warengruppen und Suche. Ursprünglich Teil der einzelnen Dashboard-Seite
 * (Statistics/Index.jsx mit drei Reitern) - seit der Aufteilung in eigene
 * Unterseiten (August 2026) hier zentral, damit jede Seite exakt dasselbe
 * Filterverhalten zeigt, ohne die Logik fünfmal zu duplizieren.
 *
 * Lädt beim Einbinden die Filteroptionen (/statistics/filters) sowie eine
 * vom Zeitraum-Filter UNABHÄNGIGE Warengruppen-Rangfolge (nach Gesamtumsatz,
 * /statistics/by-group) - daraus ergibt sich eine dauerhafte Farbzuordnung
 * je Warengruppe, die kein Filter verschiebt (siehe Diagramm-Farbregeln in
 * format.js).
 */
export default function useSalesFilters() {
    const [filtersMeta, setFiltersMeta] = useState(null);
    const [filtersMetaError, setFiltersMetaError] = useState(null);
    const [groupOrder, setGroupOrder] = useState([]);

    const [preset, setPreset] = useState("last12");
    const [dateFrom, setDateFrom] = useState(null);
    const [dateTo, setDateTo] = useState(null);
    const [level, setLevel] = useState("article");
    const [articleGroups, setArticleGroups] = useState([]);
    const [searchInput, setSearchInput] = useState("");
    const [search, setSearch] = useState("");

    // Merkt sich, ob der Nutzer bereits selbst einen Zeitraum gewählt hat -
    // über einen Ref statt State, weil nur der asynchrone Callback unten ihn
    // liest. Trifft die Voreinstellungs-Antwort aus /statistics/filters erst
    // NACH einer solchen Wahl ein, darf sie diese nicht mehr überschreiben.
    const userHasChosenDateRangeRef = useRef(false);

    useEffect(() => {
        let active = true;

        axios
            .get("/statistics/filters")
            .then(({data}) => {
                if (!active) {
                    return;
                }
                setFiltersMeta(data);
                if (!userHasChosenDateRangeRef.current) {
                    const range = computePresetRange("last12", data);
                    if (range) {
                        setDateFrom(range.from);
                        setDateTo(range.to);
                    }
                }
            })
            .catch((e) => {
                if (!active) {
                    return;
                }
                setFiltersMetaError(e.response?.data?.message || e.message || "Die Filteroptionen konnten nicht geladen werden.");
                if (!userHasChosenDateRangeRef.current) {
                    const today = new Date();
                    const todayIso = toISODate(today.getFullYear(), today.getMonth() + 1, today.getDate());
                    setDateFrom(addMonthsISO(todayIso, -12));
                    setDateTo(todayIso);
                }
            });

        // Bewusst ohne Filter abgefragt - Grundlage der dauerhaften
        // Warengruppen-Farbzuordnung (siehe Klassenkommentar oben).
        axios
            .get("/statistics/by-group")
            .then(({data}) => {
                if (active) {
                    setGroupOrder((data?.rows || []).map((row) => row.article_group).filter(Boolean));
                }
            })
            .catch(() => {});

        return () => {
            active = false;
        };
    }, []);

    useEffect(() => {
        const timeout = setTimeout(() => setSearch(searchInput.trim()), 350);
        return () => clearTimeout(timeout);
    }, [searchInput]);

    const selectPreset = useCallback(
        (nextPreset) => {
            userHasChosenDateRangeRef.current = true;
            const range = computePresetRange(nextPreset, filtersMeta);
            setPreset(nextPreset);
            if (range) {
                setDateFrom(range.from);
                setDateTo(range.to);
            }
        },
        [filtersMeta]
    );

    const changeDateFrom = useCallback((value) => {
        userHasChosenDateRangeRef.current = true;
        setPreset("custom");
        setDateFrom(value);
    }, []);

    const changeDateTo = useCallback((value) => {
        userHasChosenDateRangeRef.current = true;
        setPreset("custom");
        setDateTo(value);
    }, []);

    const colorMap = useMemo(() => buildGroupColorMap(groupOrder), [groupOrder]);

    const filters = useMemo(
        () => ({
            date_from: dateFrom || undefined,
            date_to: dateTo || undefined,
            level,
            article_groups: articleGroups,
            search: search || undefined,
        }),
        [dateFrom, dateTo, level, articleGroups, search]
    );

    const groupOptions = filtersMeta?.article_groups || [];
    // Solange kein Zeitraum feststeht (Meta-Daten noch nicht geladen), sind
    // weder Filterleiste noch Auswertungen sinnvoll darstellbar - sonst würde
    // kurz mit "heute" statt dem jüngsten Beleg im Datenbestand abgefragt.
    const filtersReady = Boolean(dateFrom && dateTo);

    return {
        filtersMeta,
        filtersMetaError,
        groupOrder,
        colorMap,
        groupOptions,
        preset,
        dateFrom,
        dateTo,
        selectPreset,
        changeDateFrom,
        changeDateTo,
        level,
        setLevel,
        articleGroups,
        setArticleGroups,
        searchInput,
        setSearchInput,
        search,
        filters,
        filtersReady,
    };
}
